import type { LayoutItem, LayoutJob, LayoutJobListItem, LayoutSubmissionResult } from './types'

export function jobDetailsHref(job: Pick<LayoutJobListItem, 'id' | 'method'>): string {
  return job.method === 'duplication'
    ? `/layout-generator/duplicate/${job.id}`
    : `/layout-generator/jobs/${job.id}`
}

export function shouldPollJob(job: Pick<LayoutJob, 'status'> | null): boolean {
  return Boolean(job && ['planning', 'queued', 'running'].includes(job.status))
}

export function isTerminalJobStatus(status: LayoutJob['status']): boolean {
  return ['completed', 'completed_with_errors', 'cancelled'].includes(status)
}

export function completedItemCount(items: LayoutItem[]): number {
  return items.filter((item) => ['ready', 'needs_review', 'failed'].includes(item.generationStatus))
    .length
}

export function executionProgress(job: Pick<LayoutJob, 'items' | 'totalCount'>): number {
  if (!job.totalCount) return 0
  return Math.min(100, Math.round((completedItemCount(job.items) / job.totalCount) * 100))
}

export function canSubmitItem(item: LayoutItem): boolean {
  return (
    item.generationStatus === 'ready' &&
    Boolean(item.currentRevision) &&
    (item.catalogStatus === 'not_submitted' || item.catalogStatus === 'returned')
  )
}

export function canWorkWithRevision(item: LayoutItem): boolean {
  return Boolean(
    item.currentRevision &&
    ['ready', 'needs_review'].includes(item.generationStatus) &&
    !['on_acceptance', 'accepted'].includes(item.catalogStatus),
  )
}

export function hasBlockingValidation(item: LayoutItem): boolean {
  return Boolean(item.currentRevision?.validation.some((issue) => issue.level === 'error'))
}

export function selectedRevisionIds(
  items: LayoutItem[],
  selectedItemIds: Iterable<string>,
): string[] {
  const selected = new Set(selectedItemIds)
  return items
    .filter((item) => selected.has(item.id) && canSubmitItem(item))
    .map((item) => item.currentRevision!.id)
    .sort()
}

export function applySubmissionResults(
  items: LayoutItem[],
  results: LayoutSubmissionResult[],
): LayoutItem[] {
  const byItem = new Map(results.map((result) => [result.itemId, result]))
  return items.map((item) => {
    const result = byItem.get(item.id)
    if (!result) return item
    if (result.status === 'failed') {
      return {
        ...item,
        error: result.error || {
          code: 'SUBMISSION_FAILED',
          message: 'Не удалось отправить макет',
          retryable: false,
        },
      }
    }
    return {
      ...item,
      catalogStatus: result.status,
      designCode: result.designCode ?? item.designCode,
      error: result.error ? { ...result.error, retryable: false } : item.error,
    }
  })
}
